import { c as _c } from "react/compiler-runtime";
import ActionCard from '@precis-digital/kurama/ActionCard';
import CancelIcon from '@precis-digital/kurama/icons/CancelIcon';
import Chip from '@precis-digital/kurama/Chip';
import { CopilotActionCard } from '@precis-digital/kurama/MessageLayout';
import DestinationIcon from '@precis-digital/kurama/icons/DestinationIcon';
import Grid from '@precis-digital/kurama/Grid';
import ProjectsIcon from '@precis-digital/kurama/icons/ProjectsIcon';
import Typography from '@precis-digital/kurama/Typography';
import { styled } from '@precis-digital/kurama/utils';
import { type ReactElement } from 'react';
import { useChangePath } from 'shared/components/Router';
import { useCopilot } from 'shared/context/CopilotContext';
import { findCopilotNavigationTarget, getCopilotNavigationTargetDisplay } from 'shared/copilot/tools/navigationCatalog';
import { useTranslation } from 'shared/translations';
import { buildHitlDecisionActions, useCopilotHitlDecision, type CopilotHitlDecision } from '../hitlDecision';
import { buildChangeUrlDecisionResult, buildNavigateToProjectDecisionResult, parseChangeUrlArgs, parseChangeUrlResult } from './parseChangeUrl';
export interface ChangeUrlToolCallCardProps {
  args?: Record<string, unknown>;
  toolCallId?: string;
  /** Pending (or later) HITL tool result content from AG-UI. */
  resultContent?: unknown;
  /**
   * Tool that produced this HITL card. Defaults to `change_url`; also used for
   * `navigate_to_project` (dynamic Creative Assets destinations).
   */
  toolName?: 'change_url' | 'navigate_to_project';
  /** Legacy prop — prefer `args.targetId`. Kept so stale HMR parents don't crash. */
  targetId?: string;
}

/**
 * HITL card for in-app navigation (`change_url` and `navigate_to_project`):
 * Approve/Reject before navigating, then an applied action card or cancelled chip.
 */
const ChangeUrlToolCallCard = t0 => {
  const $ = _c(21);
  const {
    args,
    toolCallId,
    resultContent,
    toolName: t1,
    targetId: legacyTargetId
  } = t0;
  const toolName = t1 === undefined ? "change_url" : t1;
  const isProjectNav = toolName === "navigate_to_project";
  const t2 = isProjectNav ? "copilot.toolCall.navigateToProject" : "copilot.toolCall.changeUrl";
  let t3;
  if ($[0] !== t2) {
    t3 = {
      keyPrefix: t2
    };
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const {
    t
  } = useTranslation("common", t3);
  const {
    sendToolResult
  } = useCopilot();
  const {
    changePath
  } = useChangePath();
  const {
    targetId,
    replace: replaceFromArgs
  } = parseChangeUrlArgs(args ?? (legacyTargetId != null ? {
    targetId: legacyTargetId
  } : undefined));
  const parsedResult = parseChangeUrlResult(resultContent);
  const catalogTarget = !isProjectNav && targetId != null ? findCopilotNavigationTarget(targetId) : undefined;
  const resolvedTarget = parsedResult.target ?? (catalogTarget != null ? {
    id: catalogTarget.id,
    label: catalogTarget.label,
    path: catalogTarget.path
  } : null);
  const replace = parsedResult.replace || replaceFromArgs;
  const catalogDisplay = getCopilotNavigationTargetDisplay(resolvedTarget?.id ?? targetId);
  const label = isProjectNav ? resolvedTarget?.label ?? t("projectFallback") : catalogDisplay.label;
  const icon = isProjectNav ? <ProjectsIcon /> : catalogDisplay.icon;
  const ActionIcon = isProjectNav ? ProjectsIcon : DestinationIcon;
  const {
    decision,
    startDecision
  } = useCopilotHitlDecision(parsedResult.status);
  const handleDecision = next => {
    if (resolvedTarget == null || !startDecision(next)) {
      return;
    }
    if (next === "approved") {
      changePath(resolvedTarget.path, undefined, replace);
    }
    if (toolCallId == null || toolCallId.length === 0) {
      return;
    }
    if (isProjectNav) {
      const project = parsedResult.project;
      if (project == null) {
        return;
      }
      sendToolResult({
        toolCallId,
        toolName,
        result: {
          ...buildNavigateToProjectDecisionResult({
            decision: next,
            project,
            replace
          })
        }
      });
      return;
    }
    sendToolResult({
      toolCallId,
      toolName,
      result: {
        ...buildChangeUrlDecisionResult({
          decision: next,
          target: resolvedTarget,
          replace
        })
      }
    });
  };
  if (decision === "approved") {
    let t4;
    if ($[2] !== ActionIcon) {
      t4 = <ActionIcon />;
      $[2] = ActionIcon;
      $[3] = t4;
    } else {
      t4 = $[3];
    }
    let t5;
    if ($[4] !== t) {
      t5 = t("changedPageTo");
      $[4] = t;
      $[5] = t5;
    } else {
      t5 = $[5];
    }
    return <CopilotActionCard icon={t4} label={t5} detail={<Chip avatar={icon} label={label} />} />;
  }
  if (decision === "rejected") {
    let t4;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <CancelIcon />;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    let t5;
    if ($[7] !== t) {
      t5 = t("rejectedChip");
      $[7] = t;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    let t6;
    if ($[9] !== t5) {
      t6 = <Chip avatar={t4} label={t5} variant="semantic-neutral" />;
      $[9] = t5;
      $[10] = t6;
    } else {
      t6 = $[10];
    }
    return t6;
  }
  if (parsedResult.status === "failed" || resolvedTarget == null) {
    let t4;
    if ($[11] !== ActionIcon) {
      t4 = <ActionIcon />;
      $[11] = ActionIcon;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    let t5;
    if ($[13] !== t) {
      t5 = t("failedLabel");
      $[13] = t;
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    return <CopilotActionCard icon={t4} label={t5} detail={<Typography variant="body3" color="secondary">{t("unknownTarget", {
        targetId: targetId ?? "\u2014"
      })}</Typography>} />;
  }
  let t4;
  if ($[15] !== t) {
    t4 = t("title");
    $[15] = t;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== t) {
    t5 = t("description");
    $[17] = t;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== t5) {
    t6 = <Typography variant="body2" color="secondary">{t5}</Typography>;
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  return <ActionCard title={t4} variant="semantic-info" description={<StyledDescription>{t6}<Chip avatar={icon} label={label} /></StyledDescription>} actions={buildHitlDecisionActions({
    onDecision: handleDecision,
    approveLabel: t("approve"),
    rejectLabel: t("reject")
  })} />;
};
const StyledDescription = styled(Grid)(({
  theme
}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  width: '100%',
  alignItems: 'flex-start'
}));
export default ChangeUrlToolCallCard;